/*
This is a special hook that loads in the options and mail message data
We can then run reply, reply-all- forward, filing, etc functions against them
I've seperated it from the personal emails part as code is duplicated all over the place here
*/

// TODO: I know this is duplicated but at least its contained in one file!
// Worked ~80hours this week and I just need this to work...

import _ from "lodash";
import React from "react";
import toast from "react-hot-toast";
import { useAuth } from "react-oidc-context";
import { EmailDisplayContents } from "../components/emails/types";
import { openFileEmailModal } from "../components/modals/email-file";
import { openMailPopout } from "../components/modals/email-popout";
import { toNString } from "../helpers/guids";
import { useUnFileMailMutation } from "../services/useremails";
import { FiledEmailType } from "../types/EmailType";
import { MailMessageData, MailMessageRecipientData } from "../types/MailMessageData";

export type SenderOptions = {
    userId: string
    emailAddress: string
};

export type MailMessageClientOptions = {
    masterAccountId: string
    projectId?: string | null | undefined
}

export type MailMessageControlOptions = {
    folderId?: string | null | undefined,
    searchTerm?: string | null | undefined
};

export type useMailMessageControlProps = {
    onForward: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
    onReply: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
    onReplyAll: (clientOptions: MailMessageClientOptions | null) => Promise<MailMessage>
};

export type useFiledMailMessageControlProps = useMailMessageControlProps & {
    onUnFile: (email: EmailDisplayContents) => Promise<void>;
    onReFile: (email: EmailDisplayContents) => Promise<MailMessageData>;
}

export type useFileableMailMessageControlProps = useMailMessageControlProps & {};

const buildRecipientsList = (d: SenderOptions, v: MailMessageRecipientData[]) : MailMessageRecipient[] => {
    // flat-pack these email address values
    // how graph gives you them is a little odd
    const coreRecipients : { name: string, address: string }[] = v.filter(({ emailAddress: { address } }) => address !== d.emailAddress).map(e => ({
        name: e.emailAddress.name,
        address: e.emailAddress.address
    }));

    // make sure who we are sending too is unique
    const uniqRecipients = _.uniqBy(coreRecipients, 'address');
    return uniqRecipients.map((el: any) => ({
        contactName: el.name,
        contactEmailAddress: el.address,
        isNew: false,
        isRestricted: false
    }));
};

export const useFiledMailMessageControls = (
    mailMessage: FiledEmailType | null,
    masterAccountId: string,
    options: MailMessageControlOptions | null = null
): useFiledMailMessageControlProps => {
    const { user } = useAuth();
    const [unFileMailTrigger] = useUnFileMailMutation();
    const senderOptions: SenderOptions = {
        userId: toNString(user!.profile.sub),
        emailAddress: user!.profile.email ?? ""
    };

    const onUnFile = async (email: EmailDisplayContents): Promise<void> => {
        return await toast.promise(unFileMailTrigger({
            mailId: email.id,
            masterAccountId
        }).unwrap(), {
            loading: "Removing email from file...",
            error: (err) => err?.data?.message ?? "Unable to remove filed email. Please try again later.",
            success: `Filed email has been removed.`
        });
    };

    const onForward = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('forward', {
            parentMailMessageId: mailMessage!.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `FW: ${mailMessage!.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: [],
            ccRecipients: [],
        });
    };

    const onReply = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply', {
            parentMailMessageId: mailMessage!.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage!.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList(senderOptions, [{ emailAddress: { address: mailMessage!.senderEmailAddress!, name: mailMessage!.senderEmailAddress! } }]),
            ccRecipients: [],
            bccRecipients: []
        });
    };

    const onReplyAll = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply-all', {
            parentMailMessageId: mailMessage!.graphId,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage!.subject}`,
            body: '',
            from: toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList(senderOptions, mailMessage!.recipientEmails.map(el => ({
                emailAddress: {
                    address: el,
                    name: el
                }
            }))),
            ccRecipients: [],
            bccRecipients: []
        })
    };

    const onReFile = async (email: EmailDisplayContents): Promise<MailMessageData> => {
        return openFileEmailModal(email, null)
    }

    return {
        onUnFile,
        onForward,
        onReply,
        onReplyAll,
        onReFile
    };
}

const useMailMessageControls = (
    mailMessage: MailMessageData | null,
    delegate: UserDelegate | null = null,
    options: MailMessageControlOptions | null = null
) : useFileableMailMessageControlProps => {
    const { user } = useAuth();
    const senderOptions : SenderOptions = React.useMemo(() => {
        if (delegate) {
            return {
                userId: delegate!.userId,
                emailAddress: delegate!.userEmailAddress
            }
        }
        return  {
            userId: toNString(user!.profile.sub),
            emailAddress: user!.profile.email ?? ""
        };
    }, [ delegate, user ]);

    const onForward = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('forward', {
            parentMailMessageId: mailMessage!.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `FW: ${mailMessage!.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: [],
            ccRecipients: [],
        });
    };

    const onReply = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply', {
            parentMailMessageId: mailMessage!.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage!.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList(senderOptions, [mailMessage!.from]),
            ccRecipients: [],
            bccRecipients: []
        });
    };

    const onReplyAll = (clientOptions: MailMessageClientOptions | null = null) : Promise<MailMessage> => {
        return openMailPopout('reply-all', {
            parentMailMessageId: mailMessage!.id,
            masterAccountId: clientOptions?.masterAccountId,
            projectId: clientOptions?.projectId,
            subject: `RE: ${mailMessage!.subject}`,
            body: '',
            from: delegate?.userId ?? toNString(user!.profile.sub),
            attachments: [],
            toRecipients: buildRecipientsList(senderOptions, [...mailMessage!.toRecipients]),
            ccRecipients: buildRecipientsList(senderOptions, [...mailMessage!.ccRecipients]),
            bccRecipients: buildRecipientsList(senderOptions, [...mailMessage!.bccRecipients])
        })
    };

    return {
        onForward,
        onReply,
        onReplyAll
    };
};

export default useMailMessageControls;